import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import AnimatedLogoLineProgress from "./AnimatedLogoLineProgress";

const clamp = (n: number) => Math.max(0, Math.min(100, n));

export type LoadingScreenProps = {
 
  footerText?: string;

 
  controlledProgress?: number;

 
  tickMs?: number; 
  deltaPerTick?: number; 
  fadeOutMs?: number; 
  gapMs?: number; 

  
  containerSize?: number; 
  logoWidth?: number;
  logoHeight?: number;
};

const LoadingScreenABPBeta: React.FC<LoadingScreenProps> = ({
  footerText = "Preparando tu experiencia educativa...",
  controlledProgress,

  tickMs = 100,
  deltaPerTick = 1.25,
  fadeOutMs = 220,
  gapMs = 80,

  containerSize = 280,
  logoWidth = 224,
  logoHeight = 208,
}) => {
  const [internalProgress, setInternalProgress] = useState(0);

  // Control de fade del logo por ciclo
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const [logoCycleKey, setLogoCycleKey] = useState(0);

  // Refs para evitar aceleración
  const intervalRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);
  const isResettingRef = useRef(false);

  const progress = useMemo(() => {
    if (typeof controlledProgress === "number") return clamp(controlledProgress);
    return clamp(internalProgress);
  }, [controlledProgress, internalProgress]);

  // Ciclo de progreso con fade-out/in al finalizar
  useEffect(() => {
    if (typeof controlledProgress === "number") return;

    const TOTAL_PAUSE = fadeOutMs + gapMs;

    const clearAll = () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
      isResettingRef.current = false;
    };

    clearAll();

    intervalRef.current = window.setInterval(() => {
      setInternalProgress((prev) => {
        if (isResettingRef.current) return prev;

        const next = prev + deltaPerTick;

        if (next >= 100) {
          isResettingRef.current = true;

     
          setIsLogoVisible(false);


          if (resetTimeoutRef.current) {
            window.clearTimeout(resetTimeoutRef.current);
          }

          resetTimeoutRef.current = window.setTimeout(() => {
         
            setInternalProgress(0);

         
            setLogoCycleKey((k) => k + 1);

      
            setIsLogoVisible(true);

            isResettingRef.current = false;
          }, TOTAL_PAUSE);

          return 100;
        }

        return next;
      });
    }, tickMs);

    return clearAll;
  }, [controlledProgress, tickMs, deltaPerTick, fadeOutMs, gapMs]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#FCFCFC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Montserrat",
        px: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
        }}
      >
        {/* Contenedor del logo */}
        <Box
          sx={{
            position: "relative",
            width: containerSize,
            height: containerSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Logo con progreso interno */}
          <AnimatePresence mode="wait">
            {isLogoVisible && (
              <motion.div
                key={logoCycleKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: fadeOutMs / 1000 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AnimatedLogoLineProgress
                  progress={progress}
                  width={logoWidth}
                  height={logoHeight}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Texto inferior */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
        >
          <Typography
            sx={{
              color: "#336B87",
              textAlign: "center",
              fontSize: { xs: "0.98rem", sm: "1.25rem" },
              maxWidth: 520,
              leading: {
                relaxed: '1.625',
              }
            }}
          >
            {footerText}
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );
};

export default LoadingScreenABPBeta;
